import { useCallback, useEffect, useState } from 'react';

const THEME_KEY = 'jetpacker-theme';

function getStoredTheme(): boolean {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark';
  } catch {
    return false;
  }
}

function applyTheme(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  // Update browser theme-color for mobile address bar
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = isDark ? '#0f172a' : '#49A2F3';
  }
}

// Module-level state shared across all hook instances
let globalIsDark = getStoredTheme();
const listeners = new Set<(isDark: boolean) => void>();

// Apply synchronously on module load to prevent FOUC
applyTheme(globalIsDark);

/**
 * Hook to manage dark/light theme.
 * Module-level state ensures all instances stay in sync without a context provider.
 */
export function useTheme() {
  const [isDark, setIsDark] = useState(() => globalIsDark);

  useEffect(() => {
    // Register this instance as a listener for external toggles
    listeners.add(setIsDark);
    // Sync in case state changed between render and mount
    setIsDark(globalIsDark);
    return () => {
      listeners.delete(setIsDark);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    globalIsDark = !globalIsDark;
    try {
      localStorage.setItem(THEME_KEY, globalIsDark ? 'dark' : 'light');
    } catch {
      // localStorage unavailable
    }
    applyTheme(globalIsDark);
    // Notify all mounted hook instances
    listeners.forEach((listener) => listener(globalIsDark));
  }, []);

  return { isDark, toggleTheme } as const;
}
